import { action, computed, observable } from 'mobx';
import { HttpClient } from 'Mri.Web.Common';

export interface MetricPoint {
    /** ISO-8601 UTC instant the sample was taken. */
    t: string;
    /** CPU use in millicores. */
    cpu: number;
    /** Memory use in bytes. */
    memory: number;
}

export interface MetricSeries {
    name: string;
    app: string | null;
    node: string | null;
    /** Millicores the series is measured against (node allocatable / pod limit), or null when unbounded. */
    cpuCapacity: number | null;
    /** Bytes the series is measured against, or null when the pod declares no memory limit. */
    memoryCapacity: number | null;
    points: MetricPoint[];
    /** Newest raw sample, before bucket-averaging — what "currently using" should quote. */
    latest: MetricPoint | null;
}

export interface ClusterMetrics {
    available: boolean;
    unavailableReason: string | null;
    nodesAvailable: boolean;
    nodesUnavailableReason: string | null;
    sampleIntervalSeconds: number;
    retentionHours: number;
    oldestSample: string | null;
    newestSample: string | null;
    pods: MetricSeries[];
    nodes: MetricSeries[];
}

export interface RangeOption {
    readonly label: string;
    readonly hours: number;
}

/** One week is the cap — both what the collector retains and the longest view offered. */
export const RANGE_OPTIONS: readonly RangeOption[] = [
    { label: '1 hour', hours: 1 },
    { label: '6 hours', hours: 6 },
    { label: '24 hours', hours: 24 },
    { label: '7 days', hours: 24 * 7 },
];

const EMPTY: ClusterMetrics = {
    available: false,
    unavailableReason: null,
    nodesAvailable: false,
    nodesUnavailableReason: null,
    sampleIntervalSeconds: 60,
    retentionHours: 24 * 7,
    oldestSample: null,
    newestSample: null,
    pods: [],
    nodes: [],
};

export class PodChartsStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @observable
    public metrics: ClusterMetrics = EMPTY;

    @observable
    public loading = false;

    /** True once a response has been seen. Until then the page must not assert anything about the cluster. */
    @observable
    public loaded = false;

    @observable
    public error: string | null = null;

    /** Defaults to the full week the page is capped at. */
    @observable
    public hours: number = 24 * 7;

    /** Pods grouped by their `app` label, so a deployment's pods sit together. */
    @computed
    public get podsByApp(): { app: string; pods: MetricSeries[] }[] {
        const groups = new Map<string, MetricSeries[]>();

        for (const pod of this.metrics.pods) {
            const key = pod.app ?? 'unlabelled';
            const existing = groups.get(key);
            if (existing) {
                existing.push(pod);
            } else {
                groups.set(key, [pod]);
            }
        }

        return Array.from(groups.entries()).map(([app, pods]) => ({ app, pods }));
    }

    /**
     * The window one series' chart draws.
     *
     * The selected range is a CAP, not a fixed span: the left edge is pulled in to where THAT series'
     * history actually begins. Trimming against the globally oldest sample instead is not enough — a pod
     * redeployed five minutes ago would still be drawn on a week-wide axis just because some other pod has
     * been collecting all week, which is the same near-empty chart this is meant to avoid.
     *
     * The cost is that cards can have different x-axis spans, so compare timestamps rather than widths.
     */
    public windowFor(series: MetricSeries): { from: Date; to: Date } {
        const to = this.metrics.newestSample ? new Date(this.metrics.newestSample) : new Date();
        const requestedFrom = to.getTime() - this.hours * 60 * 60 * 1000;

        const first = series.points.length > 0 ? new Date(series.points[0].t).getTime() : null;
        // Whichever is later: the range asked for, or where this series' own data starts.
        let from = first != null ? Math.max(requestedFrom, first) : requestedFrom;

        // Right after startup there may be one or two samples; without a floor the axis collapses to a
        // near-zero span and the chart reads as broken rather than as new.
        const floor = MIN_SPAN_SAMPLES * (this.metrics.sampleIntervalSeconds || 60) * 1000;
        if (to.getTime() - from < floor) {
            from = to.getTime() - floor;
        }

        return { from: new Date(from), to };
    }

    @action
    public setHours(hours: number) {
        this.hours = hours;
        // Fire and forget — the caller is an onClick, and loadAsync surfaces its own errors.
        void this.loadAsync();
    }

    @action
    public async loadAsync() {
        this.loading = true;
        this.error = null;
        try {
            const result = await this.httpClient.get<ClusterMetrics>(
                `/api/v1/infrastructure/metrics?hours=${this.hours}&maxPoints=${MAX_POINTS}`
            );
            this.metrics = result ?? EMPTY;
            this.loaded = true;
        } catch (e: any) {
            this.error = e?.body?.message ?? e?.message ?? 'Could not load pod metrics';
        } finally {
            this.loading = false;
        }
    }

    private readonly httpClient: HttpClient;
}

// Enough to resolve a spike at 150px tall without shipping a week of per-minute samples per pod.
const MAX_POINTS = 300;

// Narrowest x-axis the charts will draw, in sample intervals — keeps the very first minutes readable.
const MIN_SPAN_SAMPLES = 5;
